import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { triggerConfetti } from "../../../design-system/utils/Celebration/confetti.jsx";
import { UserTour } from "../../../design-system/components/PopOver/UserTour/UserTour.jsx";
import { MODELS, useModel } from "../../../api";
import { buildStepsForUserTour } from "./config/StepsDataForUserTour.jsx";
import { useWorkshopTourContext } from "./context/WorkshopUserTourContext.jsx";
import { setFeedbackTrigger } from "../../feedback/feedbackTriggerStore";

/**
 * WorkshopUserTour component handles the guided user tour logic for the workshop application.
 * It initializes, manages, and tracks the state of a multi-step user tour (e.g., onboarding or feature explanation).
 * The tour responds to query parameters, UI interactions, and routing changes, ensuring smooth rendering and user guidance.
 *
 * The component performs the following tasks:
 * - Listens for a specific query parameter ("tour") to determine if a tour should be initiated.
 * - Sets up the tour steps using a context state and initializes the tour.
 * - Handles step transitions, either automatically or when triggered by user actions (e.g., next, previous, or skipping the tour).
 * - Manages retry logic to ensure DOM elements required for steps are available before proceeding.
 * - Ensures smooth navigation when routes switch between tour steps, with error handling and retries.
 * - Cleans up resources (e.g., timers) to prevent memory leaks when the component unmounts.
 *
 * @param {Object} props
 * @param {import("../sparkBag").SparkBag} props.bag - The spark data `SparkShell` resolved.
 */
export const WorkshopUserTour = ({ bag: { spark, settings } }) => {
  const sparkSteps = useModel(MODELS.SPARK_SCHEDULE_STEPS(spark.slug, null, 1));
  const firstStep = sparkSteps?.[0];
  const stepSlug = firstStep?.slug;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tourInitialized, setTourInitialized] = useState(false);

  const {
    setState,
    state: { run, steps, tourActive, stepIndex },
  } = useWorkshopTourContext();

  const isFirstMount = useRef(true);

  // The tour only covers what this spark actually has: a step whose target
  // never mounts would stall waitForTarget and then abort the whole tour, and
  // copy naming a tool that isn't there would just confuse.
  const tourSteps = useMemo(
    () =>
      buildStepsForUserTour({
        library: settings?.libraryEnabled,
        glossary: spark.showGlossary,
      }),
    [settings?.libraryEnabled, spark.showGlossary],
  );

  useEffect(() => {
    if (isFirstMount.current) {
      // Restart the tour if landing on the Lernreise-Übersicht,
      // tour is desired (through query parameter "tour")
      // and tour is not already active.
      if (!tourActive) {
        setState({
          run: true,
          tourActive: true,
          stepIndex: 0,
          steps: tourSteps,
        });
      }
      setTourInitialized(true);
      isFirstMount.current = false;
    }
  }, [setState, tourActive, tourSteps]);

  const retryTimeoutRef = useRef(null); // To manage timeout cleanup
  const retryAttemptsRef = useRef(0); // Track retry attempts

  // Retry logic to wait for the target
  const waitForTarget = async (targetSelector) => {
    return new Promise((resolve) => {
      retryAttemptsRef.current = 0;

      const checkTarget = () => {
        const target = document?.querySelector(targetSelector);

        if (target) {
          clearTimeout(retryTimeoutRef.current); // Stop retries if target is found
          retryTimeoutRef.current = null; // Clear reference
          resolve(true);
        } else {
          retryAttemptsRef.current += 1;

          if (retryAttemptsRef.current <= 10) {
            retryTimeoutRef.current = setTimeout(checkTarget, 500); // Retry every 500ms
          } else {
            console.error(
              `Target ${targetSelector} not found after maximum retries.`,
            );
            retryTimeoutRef.current = null;
            resolve(false); // Fail after max retries
          }
        }
      };

      checkTarget();
    });
  };

  const handleCallback = async (data) => {
    const { index, action, status, type, step } = data;

    // UserTour states
    const isFinished = status === "finished";
    const isSkipped = status === "skipped";

    // Callback types
    const isErrorCallback = type.startsWith("error");
    const preRenderingCallback = type === "step:before"; // "step:before" is called before the rendering of a step
    const afterRenderingCallback = type === "step:after"; // "step:after" is called when a step has been rendered

    // afterRenderingCallback actions
    const isPreviousAction = action === "prev";
    const isNextAction = action === "next";

    // End the tour
    if (isFinished || isSkipped) {
      setState({ run: false, tourActive: false });
      if (!isSkipped) triggerConfetti();
      // The tour is the last step of creating a Lernreise — a good moment to
      // ask, and the only one the host can't time by itself.
      setFeedbackTrigger("sparksCreation");
      return;
    }

    // To ensure smooth rendering when routes switch between steps,
    // we wait for the target to be mounted.
    if (preRenderingCallback) {
      const targetIsMounted = await waitForTarget(step.target);

      if (!targetIsMounted) {
        setState({ run: false, tourActive: false }); // Stop or pause tour if target fails to mount
      }
      return;
    }

    // Even when waiting for the target to be mounted, errors occur sometimes
    // when the route switches between steps and in the new route the step's target has not yet been mounted.
    // Why this is the case even though we wait for mountin in "step:before" is unknown.
    // But since it happens, we handle it gracefully, by trying again.
    if (isErrorCallback) {
      const targetIsMounted = await waitForTarget(step.target); // Wait until the target is available
      if (targetIsMounted) {
        setState({ run: false, tourActive: true });

        retryTimeoutRef.current = setTimeout(() => {
          setState({
            run: true, // Restart the tour
            stepIndex: index, // Restart the current step
          });
          retryTimeoutRef.current = null; // Clear reference
        }, 300); // Allow some time for Joyride to reset state
      } else {
        setState({ run: true, tourActive: true, stepIndex: 0 }); // Try again from the beginning.
      }
      return;
    }

    // Control change between UserTour steps
    if (afterRenderingCallback) {
      if (isNextAction || isPreviousAction) {
        let nextStepIndex = index;
        if (isNextAction) {
          nextStepIndex += 1;
        } else if (isPreviousAction) {
          nextStepIndex -= 1;
        }

        setState({ run: false }); // pause tour

        // Navigate to a different route if needed
        if (isNextAction && step.data?.next) {
          const nextPath = step.data.next(stepSlug);
          if (nextPath === -1) {
            navigate(-1);
          } else {
            navigate({ pathname: nextPath, search: searchParams.toString() });
          }
        } else if (isPreviousAction && step.data?.prev) {
          const prevPath = step.data.prev(stepSlug);
          if (prevPath === -1) {
            navigate(-1);
          } else {
            navigate({ pathname: prevPath, search: searchParams.toString() });
          }
        }

        // A step that points inside the Spark flyout can only run once the
        // flyout is on screen, and Radix portals its content a render after
        // `openSparkMenu` opens it. So hand the step index over first — that
        // opens the flyout while the tour is still paused — and only resume
        // once the portal has landed, instead of letting Joyride miss the
        // target and recover through the error path above.
        const nextStep = steps[nextStepIndex];
        if (nextStep?.data?.openSparkMenu) {
          setState({ stepIndex: nextStepIndex });
          retryTimeoutRef.current = setTimeout(() => {
            setState({ run: true });
            retryTimeoutRef.current = null;
          }, 0);
        } else {
          setState({ stepIndex: nextStepIndex, run: true }); // restart tour at new step index
        }
      }
      return;
    }
  };

  useEffect(() => {
    return () => {
      // Cleanup to prevent memory leaks when component unmounts
      clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  if (!tourInitialized || !steps || steps.length === 0) return null;

  return (
    <UserTour
      callback={handleCallback}
      steps={steps}
      run={run}
      stepIndex={stepIndex}
    />
  );
};
